"use client";

import { c as _c } from "react/compiler-runtime";
import type { Dispatch } from "react";
import { useCallback, useEffect, useRef } from "react";
import { validateInteractivePreview } from "../actions/validateInteractivePreview";
import type { InteractiveMediaMimeType, InteractiveMediaProvider } from "../constants/media";
import type { FormAction, FormState, MediaSource } from "./artworkSubmissionFormState";
import { buildExternalMediaState } from "./artworkSubmissionFormState";
interface UseArtworkSubmissionMediaControlsParams {
  readonly state: FormState;
  readonly dispatch: Dispatch<FormAction>;
}
const FILE_READ_ERROR_MESSAGE = "Unable to read the selected file. Please try again.";
const FILE_READ_ABORT_MESSAGE = "File reading was cancelled. Select the artwork again.";
export function useArtworkSubmissionMediaControls(t0) {
  const $ = _c(36);
  const {
    state,
    dispatch
  } = t0;
  let t1;
  if ($[0] !== dispatch || $[1] !== state) {
    t1 = {
      state,
      dispatch
    };
    $[0] = dispatch;
    $[1] = state;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useExternalMediaValidation(t1);
  let t2;
  if ($[3] !== dispatch) {
    t2 = mode => {
      dispatch({
        type: "SET_MEDIA_SOURCE",
        payload: mode
      });
    };
    $[3] = dispatch;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const setMediaSource = t2;
  let t3;
  if ($[5] !== dispatch || $[6] !== state.externalMedia.mimeType) {
    t3 = (input, provider) => {
      const nextExternalMedia = buildExternalMediaState(input, provider, state.externalMedia.mimeType);
      dispatch({
        type: "SET_EXTERNAL_MEDIA",
        payload: nextExternalMedia
      });
    };
    $[5] = dispatch;
    $[6] = state.externalMedia.mimeType;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const updateExternalMediaState = t3;
  let t4;
  if ($[8] !== state.externalMedia.provider || $[9] !== updateExternalMediaState) {
    t4 = hash => {
      updateExternalMediaState(hash, state.externalMedia.provider);
    };
    $[8] = state.externalMedia.provider;
    $[9] = updateExternalMediaState;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const setExternalMediaHash = t4;
  let t5;
  if ($[11] !== state.externalMedia.input || $[12] !== updateExternalMediaState) {
    t5 = provider_0 => {
      updateExternalMediaState(state.externalMedia.input, provider_0);
    };
    $[11] = state.externalMedia.input;
    $[12] = updateExternalMediaState;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const setExternalMediaProvider = t5;
  let t6;
  if ($[14] !== dispatch || $[15] !== state.externalMedia.input || $[16] !== state.externalMedia.provider) {
    t6 = mimeType => {
      const nextExternalMedia_0 = buildExternalMediaState(state.externalMedia.input, state.externalMedia.provider, mimeType);
      dispatch({
        type: "SET_EXTERNAL_MEDIA",
        payload: nextExternalMedia_0
      });
    };
    $[14] = dispatch;
    $[15] = state.externalMedia.input;
    $[16] = state.externalMedia.provider;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const setExternalMediaMimeType = t6;
  let t7;
  if ($[18] !== state.externalMedia.provider || $[19] !== updateExternalMediaState) {
    t7 = () => {
      updateExternalMediaState("", state.externalMedia.provider);
    };
    $[18] = state.externalMedia.provider;
    $[19] = updateExternalMediaState;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const clearExternalMedia = t7;
  let t8;
  if ($[21] !== dispatch) {
    t8 = file => {
      const reader = new FileReader();
      let readFailed = false;
      const setUploadError = message => {
        if (readFailed) {
          return;
        }
        readFailed = true;
        dispatch({
          type: "SET_UPLOAD_ERROR",
          payload: message
        });
      };
      dispatch({
        type: "SET_UPLOAD_ERROR",
        payload: null
      });
      reader.onerror = () => {
        setUploadError(FILE_READ_ERROR_MESSAGE);
      };
      reader.onabort = () => {
        setUploadError(FILE_READ_ABORT_MESSAGE);
      };
      reader.onloadend = () => {
        if (readFailed) {
          return;
        }
        if (typeof reader.result !== "string") {
          setUploadError(FILE_READ_ERROR_MESSAGE);
          return;
        }
        dispatch({
          type: "SET_UPLOAD_MEDIA",
          payload: {
            file,
            artworkUrl: reader.result
          }
        });
      };
      try {
        reader.readAsDataURL(file);
      } catch {
        setUploadError(FILE_READ_ERROR_MESSAGE);
      }
    };
    $[21] = dispatch;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleFileSelect = t8;
  let t9;
  if ($[23] !== dispatch || $[24] !== state.externalMedia.provider || $[25] !== state.mediaSource || $[26] !== updateExternalMediaState) {
    t9 = uploaded => {
      if (uploaded) {
        return;
      }
      if (state.mediaSource === "url") {
        updateExternalMediaState("", state.externalMedia.provider);
        return;
      }
      dispatch({
        type: "RESET_UPLOAD_MEDIA"
      });
    };
    $[23] = dispatch;
    $[24] = state.externalMedia.provider;
    $[25] = state.mediaSource;
    $[26] = updateExternalMediaState;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const setArtworkUploaded = t9;
  let t10;
  if ($[28] !== clearExternalMedia || $[29] !== handleFileSelect || $[30] !== setArtworkUploaded || $[31] !== setExternalMediaHash || $[32] !== setExternalMediaMimeType || $[33] !== setExternalMediaProvider || $[34] !== setMediaSource) {
    t10 = {
      clearExternalMedia,
      handleFileSelect,
      setArtworkUploaded,
      setExternalMediaHash,
      setExternalMediaMimeType,
      setExternalMediaProvider,
      setMediaSource
    };
    $[28] = clearExternalMedia;
    $[29] = handleFileSelect;
    $[30] = setArtworkUploaded;
    $[31] = setExternalMediaHash;
    $[32] = setExternalMediaMimeType;
    $[33] = setExternalMediaProvider;
    $[34] = setMediaSource;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  return t10;
}
function useExternalMediaValidation({
  state,
  dispatch
}: UseArtworkSubmissionMediaControlsParams): void {
  const validationRequestKeyRef = useRef<string | null>(null);
  const {
    provider,
    sanitizedHash,
    status
  } = state.externalMedia;
  useEffect(() => {
    if (state.mediaSource !== "url") {
      validationRequestKeyRef.current = null;
      return;
    }
    if (status !== "pending" || !sanitizedHash) {
      return;
    }
    const validationKey = `${provider}:${sanitizedHash}`;
    validationRequestKeyRef.current = validationKey;
    let cancelled = false;
    const runValidation = async (): Promise<void> => {
      try {
        const result = await validateInteractivePreview({
          provider,
          path: sanitizedHash
        });
        if (cancelled || validationRequestKeyRef.current !== validationKey) {
          return;
        }
        if (result.ok) {
          dispatch({
            type: "SET_EXTERNAL_MEDIA_VALIDATION",
            payload: {
              status: "valid",
              error: null,
              finalUrl: result.finalUrl
            }
          });
        } else {
          dispatch({
            type: "SET_EXTERNAL_MEDIA_VALIDATION",
            payload: {
              status: "invalid",
              error: result.reason ?? "Interactive media must respond with an HTML document."
            }
          });
        }
        validationRequestKeyRef.current = null;
      } catch (error) {
        console.error("[useArtworkSubmissionForm] validateInteractivePreview failed", {
          provider,
          sanitizedHash,
          error
        });
        if (cancelled || validationRequestKeyRef.current !== validationKey) {
          return;
        }
        dispatch({
          type: "SET_EXTERNAL_MEDIA_VALIDATION",
          payload: {
            status: "invalid",
            error: "Unable to verify media URL. Try again later."
          }
        });
        validationRequestKeyRef.current = null;
      }
    };
    void runValidation();
    return () => {
      cancelled = true;
    };
  }, [dispatch, provider, sanitizedHash, state.mediaSource, status]);
}